import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { DropdownItem, filterStyleCompact, PublicClassNames } from 'embed/constants'
import { LoanFilterStyles } from './container'

// Filters
import { CreditScoreFilter } from 'embed/components/filters/CreditScore'
import { LoanAmount } from 'embed/components/filters/LoanAmount'
import LoanPurpose from 'embed/components/filters/LoanPurpose'
import Zipcode from 'embed/components/filters/Zipcode'

import AprTypeToggleFilter from 'embed/components/AprTypeToggleFilter'
import FilterModal from 'embed/components/FilterModal'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { ToggleFilterListStyles } from 'embed/components/ToggleFilterList'
import { Filter, Filters } from 'even-ts-static'
import SelectedFilterPills from './SelectedFilterPills'

const formStyles = require('embed/components/Form/styles.scss')
const filterStyles = require('embed/styles/filters')

export interface Props {
  creditRatingLabel?: string
  filters?: Filters
  isDotDash: boolean
  loanPurposeItems?: DropdownItem<unknown>[]
  loanPurposeOnChange: (fieldName: Filter, value, isValid: boolean) => void
  onChange: (fieldName: Filter, value, isValid: boolean) => void
  styles?: LoanFilterStyles
}

const InputStyles = {
  field: filterStyles.field,
  fieldError: formStyles.fieldError,
  fieldErrorMessage: formStyles.fieldErrorMessage,
  fieldFocus: formStyles.fieldFocus,
  fieldInput: filterStyles.input,
  fieldLabel: classnames(PublicClassNames.FormLabel, filterStyles.label),
}

const DropdownStyles = {
  dropDown: filterStyles.field,
  dropDownLabel: classnames(PublicClassNames.FormLabel, filterStyles.label),
  fieldError: formStyles.fieldError,
}

const toggleFilterListStyles: ToggleFilterListStyles = {}

const Render: React.FC<Props> = ({
  creditRatingLabel,
  filters,
  isDotDash,
  loanPurposeItems,
  loanPurposeOnChange,
  onChange,
  styles,
}) => {
  const useFilterModal = filterStyleCompact()

  const hideLoanPurpose = loanPurposeItems ? loanPurposeItems.length === 1 : false

  const useThreeFilterStyle = hideLoanPurpose && !useFilterModal

  const inputContainerClass = useFilterModal ? filterStyles.modalInputContainer : filterStyles.container

  const inputs = (
    <>
      <CreditScoreFilter
        containerClass={classnames(
          PublicClassNames.FormDropdown,
          inputContainerClass,
          useThreeFilterStyle && filterStyles.threeFiltersWide,
        )}
        creditRatingLabel={creditRatingLabel}
        onChange={onChange}
        startingValue={filters?.[Filter.CreditScore]}
        styles={DropdownStyles}
      />
      <Zipcode
        containerClass={classnames(
          PublicClassNames.FormInput,
          inputContainerClass,
          useThreeFilterStyle && filterStyles.threeFiltersWide,
        )}
        onChange={onChange}
        styles={InputStyles}
        value={filters?.[Filter.Zipcode]}
      />
      <LoanPurpose
        containerClass={classnames(PublicClassNames.FormDropdown, inputContainerClass)}
        hidden={hideLoanPurpose}
        items={loanPurposeItems || []}
        onChange={loanPurposeOnChange}
        startingValue={filters?.[Filter.LoanPurpose]}
        styles={DropdownStyles}
      />
      <LoanAmount
        containerClass={classnames(
          PublicClassNames.FormInput,
          inputContainerClass,
          useThreeFilterStyle && filterStyles.threeFiltersWide,
        )}
        onChange={onChange}
        startingValue={filters?.[Filter.LoanAmount]}
        styles={InputStyles}
      />
      {useFilterModal && (
        <AprTypeToggleFilter
          styles={{
            toggleFilterListContainer: classnames(PublicClassNames.FormInput, inputContainerClass),
            ...toggleFilterListStyles,
          }}
        />
      )}
      <SelectedFilterPills />
    </>
  )

  if (!useFilterModal) {
    return (
      <ThemeConsumer>
        {({ backdropStyles, borderRadiusLarge, boxStyles, colorBorder }) => (
          <div
            className={classnames(
              PublicClassNames.FilterContainer,
              styles?.wrapper ? styles.wrapper : filterStyles.filtersWrapper,
              css`
                background-color: ${backdropStyles.backgroundColor};
                border-color: ${colorBorder};
                border-radius: ${borderRadiusLarge};
                margin-top: 16px;
              `,
              {
                [css`
                  margin-top: 30px;
                `]: isDotDash,
              },
            )}
            style={boxStyles}
          >
            {inputs}
          </div>
        )}
      </ThemeConsumer>
    )
  } else {
    return <FilterModal className={filterStyles.modalButton}>{inputs}</FilterModal>
  }
}

Render.defaultProps = {
  creditRatingLabel: 'Credit Rating',
  styles: {
    wrapper: '',
  },
}

export default Render
